import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BookOpen, Mail, ChevronLeft, ChevronRight } from "lucide-react";
import Ornament, { SectionOverline } from "../components/Ornament";
import SmartImage from "../components/SmartImage";
import { api } from "../lib/config";
import useSEO from "../hooks/useSEO";

/**
 * Page dédiée à une réalisation (SEO friendly, URL indexable).
 * URL : /realisations/{slug}
 */
const RealisationDetailPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    setActiveImg(0);
    (async () => {
      try {
        const [d, c] = await Promise.all([
          api.get(`/gallery/${slug}`),
          api.get(`/gallery-categories`),
        ]);
        setData(d.data);
        setCats(c.data);
      } catch (e) {
        if (e.response?.status === 404) setNotFound(true);
        else console.error(e);
      }
    })();
  }, [slug]);

  const item = data?.item;
  const catLabel = cats.find((c) => c.slug === item?.category)?.label || item?.category;

  // Build image gallery (cover + extras)
  const gallery = item ? [item.image_url, ...(item.images || [])].filter(Boolean) : [];

  // SEO
  const seoTitle = item ? `${item.title}` : "Réalisation";
  const seoDesc = item
    ? (item.description ? item.description.slice(0, 155) : `Découvrez cette réalisation "${item.title}" par Passion Verre. Gravure et découpe laser sur mesure — atelier artisanal en Isère.`)
    : undefined;
  const jsonLd = item ? {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "name": item.title,
    "description": item.description || `Réalisation ${catLabel} par Passion Verre`,
    "image": gallery.map((g) => (g?.startsWith("/") ? `https://passionverre.com${g}` : g)),
    "creator": { "@type": "Organization", "name": "Passion Verre", "url": "https://passionverre.com" },
    "material": item.material || undefined,
    "genre": catLabel,
  } : null;
  useSEO({ title: seoTitle, description: seoDesc, image: item?.image_url, jsonLd });

  if (notFound) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-[#171717] px-6">
        <div className="text-center">
          <h1 className="font-serif text-4xl text-[#DDD4B2] mb-4">Réalisation introuvable</h1>
          <p className="text-[#DDD4B2]/70 mb-8">Cette création n'existe pas ou a été retirée.</p>
          <Link to="/realisations" className="btn-gold px-6 py-3 text-xs uppercase tracking-[0.18em] inline-flex items-center gap-2">
            <ArrowLeft size={14} /> Retour aux réalisations
          </Link>
        </div>
      </div>
    );
  }

  if (!item) {
    return <div className="min-h-[60vh] flex items-center justify-center text-[#DDD4B2]/60">Chargement…</div>;
  }

  const prevImg = () => setActiveImg((i) => (i === 0 ? gallery.length - 1 : i - 1));
  const nextImg = () => setActiveImg((i) => (i === gallery.length - 1 ? 0 : i + 1));

  return (
    <div data-testid="realisation-detail-page">
      {/* Breadcrumb + back */}
      <section className="pt-28 pb-4 bg-[#171717]">
        <div className="max-w-6xl mx-auto px-6">
          <Link
            to="/realisations"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#9A8640] hover:text-[#DDD4B2] transition-colors"
            data-testid="detail-back-link"
          >
            <ArrowLeft size={14} /> Retour aux réalisations
          </Link>
        </div>
      </section>

      {/* HERO — image + intro */}
      <section className="pb-16 bg-[#171717]">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Gallery */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:sticky lg:top-24"
          >
            <div className="relative aspect-square overflow-hidden bg-[#0f0f0f] border border-[#9A8640]/20">
              <SmartImage src={gallery[activeImg]} alt={`${item.title} — Passion Verre`} width={1200} eager className="absolute inset-0 w-full h-full object-cover" />
              {gallery.length > 1 && (
                <>
                  <button
                    onClick={prevImg}
                    aria-label="Image précédente"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#171717]/70 hover:bg-[#9A8640] hover:text-[#171717] text-[#DDD4B2] flex items-center justify-center transition-colors"
                    data-testid="detail-img-prev"
                  ><ChevronLeft size={20} /></button>
                  <button
                    onClick={nextImg}
                    aria-label="Image suivante"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#171717]/70 hover:bg-[#9A8640] hover:text-[#171717] text-[#DDD4B2] flex items-center justify-center transition-colors"
                    data-testid="detail-img-next"
                  ><ChevronRight size={20} /></button>
                </>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-2">
                {gallery.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`relative aspect-square overflow-hidden border-2 transition-all ${activeImg === i ? "border-[#9A8640]" : "border-transparent opacity-60 hover:opacity-100"}`}
                    aria-label={`Voir l'image ${i + 1}`}
                    data-testid={`detail-thumb-${i}`}
                  >
                    <SmartImage src={src} alt="" width={200} className="absolute inset-0 w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <SectionOverline className="mb-4">{catLabel}</SectionOverline>
            <h1 className="font-serif text-4xl sm:text-5xl text-white leading-[1.1] mb-6" data-testid="detail-title">
              {item.title}
            </h1>

            {/* Meta pills */}
            <div className="flex flex-wrap gap-2 mb-8">
              {item.material && (
                <span className="text-xs uppercase tracking-[0.15em] text-[#DDD4B2]/80 border border-[#9A8640]/40 px-3 py-1.5 rounded-full">
                  Matière : {item.material}
                </span>
              )}
              {item.technique && (
                <span className="text-xs uppercase tracking-[0.15em] text-[#DDD4B2]/80 border border-[#9A8640]/40 px-3 py-1.5 rounded-full">
                  Technique : {item.technique}
                </span>
              )}
            </div>

            {item.description && (
              <div className="text-[#DDD4B2]/85 leading-relaxed whitespace-pre-wrap text-base mb-10" data-testid="detail-description">
                {item.description}
              </div>
            )}

            <Ornament className="my-8" />

            {/* CTAs */}
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="btn-gold px-7 py-4 text-sm uppercase tracking-[0.18em] inline-flex items-center gap-3" data-testid="detail-cta-contact">
                <Mail size={16} /> Demander un projet similaire
              </Link>
              {item.blog_slug && (
                <Link
                  to={`/blog/${item.blog_slug}`}
                  className="btn-outline px-7 py-4 text-sm uppercase tracking-[0.18em] inline-flex items-center gap-3"
                  data-testid="detail-cta-blog"
                >
                  <BookOpen size={16} /> Article associé
                </Link>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* RELATED */}
      {data.related?.length > 0 && (
        <section className="py-20 bg-[#0f0f0f] border-t border-[#9A8640]/20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <SectionOverline>Vous aimerez aussi</SectionOverline>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#DDD4B2] mt-3">
                Autres réalisations en {catLabel?.toLowerCase()}
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {data.related.map((r) => (
                <Link
                  key={r.id}
                  to={`/realisations/${r.slug}`}
                  className="group relative aspect-square overflow-hidden"
                  data-testid={`related-${r.id}`}
                >
                  <SmartImage src={r.image_url} alt={`${r.title} — Passion Verre`} width={400} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#171717] via-transparent to-transparent opacity-70 group-hover:opacity-90 transition-opacity" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <p className="font-serif text-sm text-white leading-tight line-clamp-2">{r.title}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default RealisationDetailPage;
